/**
 * Publicaciones de redes embebidas en el cuerpo de una nota: reconocer el link que pega la
 * redacción, y armar la dirección del iframe que lo muestra.
 *
 * **Todo lo que sabe el sistema sobre las redes está en este archivo.** Es a propósito, y por dos
 * motivos distintos:
 *
 *  - Las direcciones de los iframes son de terceros y cambian sin avisarnos. Repartidas por el
 *    panel y el sitio, el día que una cambie hay que salir a buscarlas.
 *  - El reconocedor y la validación del contrato **tienen que coincidir**. En vez de escribir las
 *    reglas dos veces y confiar en que nadie las desincronice, `ArticleEmbedNode` valida llamando
 *    a `reconocerEmbed()`: lo que el contrato acepta es, por construcción, exactamente lo que el
 *    editor sabe producir.
 *
 * La URL canónica **se reconstruye**, no se limpia: de la dirección pegada se sacan el proveedor y
 * el id, y con eso se arma una dirección nueva. Así ningún parámetro de seguimiento (`igsh`, `s`,
 * `is_from_webapp`, …) llega a guardarse — y esos parámetros dicen de qué teléfono salió el link.
 */
/** Cómo se llama cada red cuando hay que nombrarla en pantalla. */
export const NOMBRE_DE_RED = {
    instagram: 'Instagram',
    x: 'X',
    facebook: 'Facebook',
    tiktok: 'TikTok',
};
/**
 * Los acortados que reparten los botones de "compartir" de las apps. **No se puede saber a qué
 * publicación apuntan sin pedirlos por red**, y la API no habla con terceros, así que se rechazan
 * con un motivo propio para poder pedirle a la persona el enlace largo en vez de un "no se pudo".
 */
const HOSTS_ACORTADOS = [
    'vm.tiktok.com',
    'vt.tiktok.com',
    'fb.watch',
    'instagr.am',
    't.co',
    'bit.ly',
];
const USUARIO_X = '[A-Za-z0-9_]{1,15}';
const CODIGO_INSTAGRAM = '[A-Za-z0-9_-]{5,30}';
/** El nombre de una página de Facebook, o su id numérico. */
const PAGINA_FACEBOOK = '[A-Za-z0-9.-]{1,80}';
/** Los posts modernos de Facebook traen `pfbid0…`; los viejos, un número. */
const POST_FACEBOOK = '[A-Za-z0-9]{1,120}';
export function reconocerEmbed(entrada) {
    const texto = entrada.trim();
    if (!texto || /\s/.test(texto))
        return { ok: false, motivo: 'no-reconocido' };
    let parsed;
    try {
        parsed = new URL(texto);
    }
    catch {
        return { ok: false, motivo: 'no-reconocido' };
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return { ok: false, motivo: 'no-reconocido' };
    }
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (HOSTS_ACORTADOS.includes(host))
        return { ok: false, motivo: 'acortado' };
    // Sin barra final, para que los patrones no tengan que contemplar las dos formas.
    const path = parsed.pathname.replace(/\/+$/, '');
    // El botón de compartir de la app de Facebook da `/share/p/xxxx`: es un acortado con otra ropa.
    if (esHostDeFacebook(host) && /^\/share(\/|$)/.test(path)) {
        return { ok: false, motivo: 'acortado' };
    }
    const query = (nombre) => parsed.searchParams.get(nombre);
    const embed = reconocerInstagram(host, path) ??
        reconocerX(host, path) ??
        reconocerTikTok(host, path) ??
        reconocerFacebook(host, path, query);
    return embed ? { ok: true, embed } : { ok: false, motivo: 'no-reconocido' };
}
function esHostDeFacebook(host) {
    return host === 'facebook.com' || host === 'm.facebook.com' || host === 'web.facebook.com';
}
function reconocerInstagram(host, path) {
    if (host !== 'instagram.com')
        return null;
    // `/p/`, `/reel/`, `/reels/` y `/tv/`, con o sin el usuario adelante (Instagram reparte las dos
    // formas). El shortcode es único entre posts y reels, así que el tipo no hace falta guardarlo.
    const patron = new RegExp(`^(?:/[A-Za-z0-9._]{1,30})?/(?:p|reel|reels|tv)/(${CODIGO_INSTAGRAM})$`);
    const encontrado = patron.exec(path);
    if (!encontrado?.[1])
        return null;
    return {
        provider: 'instagram',
        postId: encontrado[1],
        url: `https://www.instagram.com/p/${encontrado[1]}/`,
    };
}
function reconocerX(host, path) {
    if (host !== 'x.com' && host !== 'twitter.com' && host !== 'mobile.twitter.com')
        return null;
    // `/usuario/status/123`, y las dos formas internas: `/i/status/123` y `/i/web/status/123`.
    const patron = new RegExp(`^/(${USUARIO_X}|i)(?:/web)?/status(?:es)?/(\\d{5,25})$`);
    const encontrado = patron.exec(path);
    if (!encontrado?.[2])
        return null;
    return {
        provider: 'x',
        postId: encontrado[2],
        url: `https://x.com/${encontrado[1]}/status/${encontrado[2]}`,
    };
}
function reconocerTikTok(host, path) {
    if (host !== 'tiktok.com')
        return null;
    const encontrado = /^\/(@[A-Za-z0-9._]{1,24})\/(video|photo)\/(\d{15,25})$/.exec(path);
    if (!encontrado?.[3])
        return null;
    // Se conserva `video` o `photo`: el iframe va por id, pero el enlace de respaldo tiene que
    // apuntar a donde la publicación realmente vive.
    return {
        provider: 'tiktok',
        postId: encontrado[3],
        url: `https://www.tiktok.com/${encontrado[1]}/${encontrado[2]}/${encontrado[3]}`,
    };
}
/**
 * Facebook es el raro: **no hay id portable**. Su plugin recibe la dirección entera, así que acá
 * la canónica se arma con el path y sólo los parámetros que identifican la publicación. Como no
 * hay `postId`, esta validación del path es la única guarda que tiene el proveedor.
 */
function reconocerFacebook(host, path, query) {
    if (!esHostDeFacebook(host))
        return null;
    const conId = (sufijo) => ({
        provider: 'facebook',
        postId: null,
        url: `https://www.facebook.com${sufijo}`,
    });
    // Sin `photos`: una foto vive en `/pagina/photos/a.123/456`, con el álbum en el medio, y
    // tomar el primer segmento como id daría el álbum en vez de la publicación. Las fotos entran
    // por `photo.php?fbid=`, más abajo.
    const directo = new RegExp(`^/(${PAGINA_FACEBOOK})/(posts|videos|reel)/(${POST_FACEBOOK})$`).exec(path);
    if (directo)
        return conId(`/${directo[1]}/${directo[2]}/${directo[3]}/`);
    // `/pagina/videos/un-titulo-cualquiera/123456/`: el slug del medio es decorativo y se descarta.
    const conSlug = new RegExp(`^/(${PAGINA_FACEBOOK})/videos/[^/]+/(\\d{5,25})$`).exec(path);
    if (conSlug)
        return conId(`/${conSlug[1]}/videos/${conSlug[2]}/`);
    const fbid = query('fbid');
    if (/^\/photo(\.php)?$/.test(path) && fbid && /^\d{5,25}$/.test(fbid)) {
        return conId(`/photo.php?fbid=${fbid}`);
    }
    const storyFbid = query('story_fbid');
    const idPagina = query('id');
    if (/^\/(permalink|story)\.php$/.test(path) &&
        storyFbid &&
        /^[A-Za-z0-9]{1,120}$/.test(storyFbid) &&
        idPagina &&
        /^\d{1,25}$/.test(idPagina)) {
        return conId(`/permalink.php?story_fbid=${storyFbid}&id=${idPagina}`);
    }
    const video = query('v');
    if (/^\/watch$/.test(path) && video && /^\d{5,25}$/.test(video)) {
        return conId(`/watch/?v=${video}`);
    }
    return null;
}
/**
 * La dirección del iframe que muestra la publicación. Ninguna de las cuatro necesita token ni el
 * script del proveedor: son las que sus propios widgets terminan pidiendo.
 */
export function urlDeEmbedIframe(embed) {
    switch (embed.provider) {
        case 'instagram':
            return `https://www.instagram.com/p/${embed.postId ?? ''}/embed/`;
        case 'x':
            // `dnt=true` es el pedido de no rastrear que su propio widget manda cuando se lo configura.
            return `https://platform.twitter.com/embed/Tweet.html?id=${embed.postId ?? ''}&dnt=true`;
        case 'tiktok':
            return `https://www.tiktok.com/embed/v2/${embed.postId ?? ''}`;
        case 'facebook':
            return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(embed.url)}&show_text=true&width=${MEDIDAS.facebook.anchoMaximo}`;
    }
}
/**
 * Cada red trae su propia forma. Son medidas **iniciales**: Instagram y X mandan su alto real por
 * `postMessage` una vez cargados y la caja crece. Facebook y TikTok no lo mandan, así que se
 * quedan con esto y, si la publicación es más larga, se scrollea adentro.
 */
const MEDIDAS = {
    // Una foto cuadrada más el encabezado y la barra de acciones.
    instagram: { proporcion: '4 / 5', anchoMaximo: 658 },
    // Los 550 px son el ancho del embed oficial de un tweet.
    x: { proporcion: '3 / 2', anchoMaximo: 550 },
    facebook: { proporcion: '4 / 5', anchoMaximo: 500 },
    // Vertical 9:16, en los 605 px para los que está hecho su embed.
    tiktok: { proporcion: '9 / 16', anchoMaximo: 605 },
};
export function medidaDeEmbed(provider) {
    return MEDIDAS[provider];
}
/**
 * El origen del que puede llegar el `postMessage` con el alto real. Se compara contra
 * `event.origin` **antes** de creerle a la medida: sin eso, cualquier iframe de la página podría
 * estirar el bloque.
 */
export const ORIGEN_DEL_EMBED = {
    instagram: 'https://www.instagram.com',
    x: 'https://platform.twitter.com',
    facebook: 'https://www.facebook.com',
    tiktok: 'https://www.tiktok.com',
};
